/**
 * BrandingFormBuilder Component
 *
 * Visual form builder for branding/theme configuration.
 * Includes color pickers, font inputs, and logo URLs.
 */

import type { BrandingConfig, BrandingColors } from "@tatvex/config";
import { Image, Type, Palette, Code } from "lucide-react";
import { useCallback, useState } from "react";

import { ColorPicker } from "./ColorPicker";
import type { BrandingFormBuilderProps, FieldError } from "./types";
import { createEmptyBrandingColors } from "./types";

/**
 * BrandingFormBuilder - Visual branding configuration
 *
 * CSS Classes:
 * - branding-form: Container element
 * - branding-form__section: Section container
 * - branding-form__section-header: Section header with icon
 * - branding-form__section-title: Section title text
 * - branding-form__section-content: Section content area
 * - branding-form__field: Field container
 * - branding-form__label: Field label
 * - branding-form__input: Text input
 * - branding-form__textarea: Textarea input
 * - branding-form__colors-grid: Color pickers grid
 * - branding-form__error: Error message
 */
export function BrandingFormBuilder({
  value,
  onChange,
  onValidate,
  readOnly = false,
  errors = [],
  className = "",
  showCustomCss = true,
}: BrandingFormBuilderProps): React.JSX.Element {
  const [localErrors, setLocalErrors] = useState<readonly FieldError[]>([]);

  // Merge external and local errors
  const allErrors = [...errors, ...localErrors];

  const handleChange = useCallback(
    <K extends keyof BrandingConfig>(key: K, newValue: BrandingConfig[K]) => {
      const updated: BrandingConfig = {
        ...value,
        [key]: newValue,
      };

      // Validate if handler provided
      if (onValidate) {
        const result = onValidate(updated);
        setLocalErrors(result.errors);
      }

      onChange(updated);
    },
    [value, onChange, onValidate]
  );

  const handleColorChange = useCallback(
    (colorKey: keyof BrandingColors, colorValue: string) => {
      const updatedColors: BrandingColors = {
        ...value.colors,
        [colorKey]: colorValue,
      };

      handleChange("colors", updatedColors);
    },
    [value.colors, handleChange]
  );

  const getFieldError = (path: string): string | undefined => {
    return allErrors.find((e) => e.path === path)?.message;
  };

  const colors = value.colors ?? createEmptyBrandingColors();

  return (
    <div className={`branding-form flex flex-col gap-6 ${className}`}>
      {/* Version Info */}
      <div className="branding-form__section rounded-lg border border-gray-200 bg-white p-4">
        <div className="branding-form__field flex items-center gap-4">
          <label className="branding-form__label text-sm font-medium text-gray-700">Version</label>
          <input
            type="number"
            value={value.version}
            onChange={(e) => handleChange("version", parseInt(e.target.value, 10) || 1)}
            readOnly={readOnly}
            min={1}
            className={`branding-form__input w-20 rounded-md border border-gray-300 px-3 py-2 text-sm ${
              readOnly ? "cursor-not-allowed bg-gray-50" : ""
            }`}
          />
          {getFieldError("version") && (
            <p className="branding-form__error text-sm text-red-500">{getFieldError("version")}</p>
          )}
        </div>
      </div>

      {/* Logo Section */}
      <div className="branding-form__section rounded-lg border border-gray-200 bg-white p-4">
        <div className="branding-form__section-header mb-4 flex items-center gap-2">
          <Image className="h-5 w-5 text-gray-500" />
          <h3 className="branding-form__section-title font-medium text-gray-900">Logo & Favicon</h3>
        </div>

        <div className="branding-form__section-content flex flex-col gap-4">
          {/* Logo URL */}
          <div className="branding-form__field flex flex-col gap-1.5">
            <label className="branding-form__label text-sm font-medium text-gray-700">
              Logo URL
            </label>
            <input
              type="url"
              value={value.logoUrl ?? ""}
              onChange={(e) => handleChange("logoUrl", e.target.value || undefined)}
              readOnly={readOnly}
              placeholder="https://example.com/logo.png"
              className={`branding-form__input rounded-md border border-gray-300 px-3 py-2 text-sm ${
                readOnly ? "cursor-not-allowed bg-gray-50" : ""
              }`}
            />
            {getFieldError("logoUrl") && (
              <p className="branding-form__error text-sm text-red-500">
                {getFieldError("logoUrl")}
              </p>
            )}
          </div>

          {/* Favicon URL */}
          <div className="branding-form__field flex flex-col gap-1.5">
            <label className="branding-form__label text-sm font-medium text-gray-700">
              Favicon URL
            </label>
            <input
              type="url"
              value={value.faviconUrl ?? ""}
              onChange={(e) => handleChange("faviconUrl", e.target.value || undefined)}
              readOnly={readOnly}
              placeholder="https://example.com/favicon.ico"
              className={`branding-form__input rounded-md border border-gray-300 px-3 py-2 text-sm ${
                readOnly ? "cursor-not-allowed bg-gray-50" : ""
              }`}
            />
            {getFieldError("faviconUrl") && (
              <p className="branding-form__error text-sm text-red-500">
                {getFieldError("faviconUrl")}
              </p>
            )}
          </div>

          {/* Logo Preview */}
          {value.logoUrl && (
            <div className="branding-form__preview mt-2">
              <p className="mb-2 text-sm text-gray-500">Preview:</p>
              <img
                src={value.logoUrl}
                alt="Logo preview"
                className="max-h-16 rounded border border-gray-200 bg-gray-50 p-2"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Colors Section */}
      <div className="branding-form__section rounded-lg border border-gray-200 bg-white p-4">
        <div className="branding-form__section-header mb-4 flex items-center gap-2">
          <Palette className="h-5 w-5 text-gray-500" />
          <h3 className="branding-form__section-title font-medium text-gray-900">Brand Colors</h3>
        </div>

        <div className="branding-form__section-content">
          {/* Primary Colors Grid */}
          <div className="branding-form__colors-grid mb-6 grid grid-cols-2 gap-4 md:grid-cols-3">
            <ColorPicker
              label="Primary"
              value={colors.primary}
              onChange={(v) => handleColorChange("primary", v)}
              readOnly={readOnly}
              required
            />
            <ColorPicker
              label="Accent"
              value={colors.accent}
              onChange={(v) => handleColorChange("accent", v)}
              readOnly={readOnly}
              required
            />
            <ColorPicker
              label="Background"
              value={colors.background}
              onChange={(v) => handleColorChange("background", v)}
              readOnly={readOnly}
              required
            />
            <ColorPicker
              label="Surface"
              value={colors.surface}
              onChange={(v) => handleColorChange("surface", v)}
              readOnly={readOnly}
              required
            />
            <ColorPicker
              label="Text Primary"
              value={colors.textPrimary}
              onChange={(v) => handleColorChange("textPrimary", v)}
              readOnly={readOnly}
              required
            />
            <ColorPicker
              label="Text Secondary"
              value={colors.textSecondary}
              onChange={(v) => handleColorChange("textSecondary", v)}
              readOnly={readOnly}
              required
            />
          </div>

          {/* Status Colors */}
          <div className="mb-2 text-sm font-medium text-gray-600">Status Colors (Optional)</div>
          <div className="branding-form__colors-grid grid grid-cols-2 gap-4 md:grid-cols-3">
            <ColorPicker
              label="Error"
              value={colors.error ?? "#EF4444"}
              onChange={(v) => handleColorChange("error", v)}
              readOnly={readOnly}
            />
            <ColorPicker
              label="Success"
              value={colors.success ?? "#22C55E"}
              onChange={(v) => handleColorChange("success", v)}
              readOnly={readOnly}
            />
            <ColorPicker
              label="Warning"
              value={colors.warning ?? "#F59E0B"}
              onChange={(v) => handleColorChange("warning", v)}
              readOnly={readOnly}
            />
          </div>
        </div>
      </div>

      {/* Fonts Section */}
      <div className="branding-form__section rounded-lg border border-gray-200 bg-white p-4">
        <div className="branding-form__section-header mb-4 flex items-center gap-2">
          <Type className="h-5 w-5 text-gray-500" />
          <h3 className="branding-form__section-title font-medium text-gray-900">Typography</h3>
        </div>

        <div className="branding-form__section-content flex flex-col gap-4">
          {/* Heading Font */}
          <div className="branding-form__field flex flex-col gap-1.5">
            <label className="branding-form__label text-sm font-medium text-gray-700">
              Heading Font
            </label>
            <input
              type="text"
              value={value.fonts?.heading ?? ""}
              onChange={(e) =>
                handleChange("fonts", {
                  ...value.fonts,
                  heading: e.target.value || undefined,
                })
              }
              readOnly={readOnly}
              placeholder="Inter, system-ui, sans-serif"
              className={`branding-form__input rounded-md border border-gray-300 px-3 py-2 text-sm ${
                readOnly ? "cursor-not-allowed bg-gray-50" : ""
              }`}
            />
          </div>

          {/* Body Font */}
          <div className="branding-form__field flex flex-col gap-1.5">
            <label className="branding-form__label text-sm font-medium text-gray-700">
              Body Font
            </label>
            <input
              type="text"
              value={value.fonts?.body ?? ""}
              onChange={(e) =>
                handleChange("fonts", {
                  ...value.fonts,
                  body: e.target.value || undefined,
                })
              }
              readOnly={readOnly}
              placeholder="Inter, system-ui, sans-serif"
              className={`branding-form__input rounded-md border border-gray-300 px-3 py-2 text-sm ${
                readOnly ? "cursor-not-allowed bg-gray-50" : ""
              }`}
            />
          </div>
        </div>
      </div>

      {/* Custom CSS Section */}
      {showCustomCss && (
        <div className="branding-form__section rounded-lg border border-gray-200 bg-white p-4">
          <div className="branding-form__section-header mb-4 flex items-center gap-2">
            <Code className="h-5 w-5 text-gray-500" />
            <h3 className="branding-form__section-title font-medium text-gray-900">Custom CSS</h3>
          </div>

          <div className="branding-form__section-content">
            <textarea
              value={value.customCss ?? ""}
              onChange={(e) => handleChange("customCss", e.target.value || undefined)}
              readOnly={readOnly}
              placeholder="/* Add custom CSS here */"
              rows={8}
              className={`branding-form__textarea w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm ${
                readOnly ? "cursor-not-allowed bg-gray-50" : ""
              }`}
            />
            <p className="mt-1 text-xs text-gray-500">
              Maximum 50,000 characters. CSS will be scoped to your vertical.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export type { BrandingFormBuilderProps };
